import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './KeycloakContext';
import axios from '../api/axios';

const WorkspaceContext = createContext();

/**
 * Workspace Context Provider
 * Fetches and provides workspace context (workspace_type, workspace_role, etc.)
 * after user authentication.
 * Also listens for real-time credit-update events to refresh org pool balance
 * for org admins/owners.
 */
export function WorkspaceProvider({ children }) {
  const { keycloak, authenticated, initialized } = useAuth();
  const [workspaceContext, setWorkspaceContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use refs updated during render (synchronous) for event handler
  const isOwnerRef = useRef(false);
  const isOrganizationAdminRef = useRef(false);
  const workspaceTypeRef = useRef(null);

  // Update refs synchronously during render - this ensures they're always current
  // when any event handler fires, since render happens before effects/events
  isOwnerRef.current = workspaceContext?.isOwner || false;
  isOrganizationAdminRef.current = workspaceContext?.isOrganizationAdmin || false;
  workspaceTypeRef.current = workspaceContext?.primaryWorkspace?.workspaceType || null;

  // Fetch user context from backend
  const fetchWorkspaceContext = useCallback(async () => {
    if (!authenticated || !keycloak?.token) {
      setWorkspaceContext(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get('/user/context', {
        headers: { 'Cache-Control': 'no-cache' },
      });

      if (response.status === 200) {
        const result = response.data;
        if (result.success && result.userContext) {
          setWorkspaceContext(result.userContext);
        } else {
          console.warn('⚠️ Failed to fetch workspace context:', response.status);
          setError('Failed to fetch workspace context');
        }
      }
    } catch (err) {
      console.error('❌ Error fetching workspace context:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticated, keycloak?.token]);

  // Fetch on authentication
  useEffect(() => {
    if (initialized && authenticated) {
      fetchWorkspaceContext();
    } else if (initialized && !authenticated) {
      setWorkspaceContext(null);
      setLoading(false);
    }
  }, [initialized, authenticated, fetchWorkspaceContext]);

  // Listen for real-time credit-update events and update org pool balance
  // when the user is an org admin/owner (so their sidebar shows live org pool balance)
  useEffect(() => {
    const handleCreditUpdate = (e) => {
      const isOwner = isOwnerRef.current;
      const isOrgAdmin = isOrganizationAdminRef.current;
      const isOrgAdminOrOwner = isOwner || isOrgAdmin;
      if (isOrgAdminOrOwner && e.detail?.credit_balance !== undefined) {
        const newBalance = e.detail.credit_balance;
        setWorkspaceContext((prev) =>
          prev
            ? {
                ...prev,
                primaryWorkspace: prev.primaryWorkspace
                  ? { ...prev.primaryWorkspace, creditBalance: newBalance }
                  : null,
              }
            : prev
        );
      }
    };
    window.addEventListener('credit-update', handleCreditUpdate);
    return () => window.removeEventListener('credit-update', handleCreditUpdate);
  }, []);

  // Derived helper values
  const value = {
    // Raw context
    workspaceContext,
    loading,
    error,

    // Refresh function
    refreshWorkspaceContext: fetchWorkspaceContext,

    // Workspace type helpers
    workspaceType: workspaceContext?.primaryWorkspace?.workspaceType || null,
    workspacePlan: workspaceContext?.primaryWorkspace?.plan || null,
    isPersonalWorkspace: workspaceContext?.primaryWorkspace?.workspaceType === 'personal',
    isOrganizationWorkspace: workspaceContext?.primaryWorkspace?.workspaceType === 'organization',

    // Workspace role helpers
    workspaceRole: workspaceContext?.workspaceRole || null,
    isOwner: workspaceContext?.isOwner || false,
    isOrganizationAdmin: workspaceContext?.isOrganizationAdmin || false,
    isOrganizationUser: workspaceContext?.isOrganizationUser || false,
    isPersonalUser: workspaceContext?.isPersonalUser || false,

    // System role helpers
    systemRole: workspaceContext?.systemRole || 'user',
    isSuperadmin: workspaceContext?.isSuperadmin || false,
    isPlatformAdmin: workspaceContext?.isPlatformAdmin || false,

    // Primary workspace info
    primaryWorkspace: workspaceContext?.primaryWorkspace || null,
    workspaceId: workspaceContext?.primaryWorkspace?.workspaceId || null,
    workspaceName: workspaceContext?.primaryWorkspace?.workspaceName || null,
    workspaceCredits: workspaceContext?.primaryWorkspace?.creditBalance ?? 0,
    workspaceCreditLimit: workspaceContext?.primaryWorkspace?.creditLimit ?? 0,

    // All workspaces
    workspaces: workspaceContext?.workspaces || [],

    // Feature gating helpers
    canManageMembers:
      workspaceContext?.isSuperadmin ||
      workspaceContext?.isPlatformAdmin ||
      (workspaceContext?.primaryWorkspace?.workspaceType === 'organization' &&
        workspaceContext?.isOrganizationAdmin),
    canInviteUsers:
      workspaceContext?.isSuperadmin ||
      workspaceContext?.isPlatformAdmin ||
      (workspaceContext?.primaryWorkspace?.workspaceType === 'organization' &&
        workspaceContext?.isOrganizationAdmin),
    canEditOrgSettings:
      workspaceContext?.isSuperadmin ||
      workspaceContext?.isPlatformAdmin ||
      (workspaceContext?.primaryWorkspace?.workspaceType === 'organization' &&
        workspaceContext?.isOrganizationAdmin),
    showTeamFeatures:
      workspaceContext?.isSuperadmin ||
      workspaceContext?.isPlatformAdmin ||
      workspaceContext?.primaryWorkspace?.workspaceType === 'organization',

    // TRUE when user is an org *member* (not admin/owner) — gates billing + edit access
    isOrgMember:
      workspaceContext?.primaryWorkspace?.workspaceType === 'organization' &&
      workspaceContext?.isOrganizationUser &&
      !workspaceContext?.isOrganizationAdmin &&
      !workspaceContext?.isSuperadmin &&
      !workspaceContext?.isPlatformAdmin,

    // Members of an org cannot buy credits — only the org owner/admin can
    canBuyCredits: !(
      workspaceContext?.primaryWorkspace?.workspaceType === 'organization' &&
      workspaceContext?.isOrganizationUser &&
      !workspaceContext?.isOrganizationAdmin &&
      !workspaceContext?.isSuperadmin &&
      !workspaceContext?.isPlatformAdmin
    ),
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

/**
 * Hook to access workspace context
 */
export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}

export default WorkspaceContext;
